<script lang="ts">
	import { Section, SectionHeader, type SectionTheme } from "#lib/web-ui/primitives/index.js";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import { Tabs, TabsContent, TabsList, TabsTrigger } from "#lib/web-ui/ui/tabs/index.js";
	import { cn } from "#lib/web-ui/ui/utils.js";
	import { RichText, type RichTextSegment } from "./glossary";

	interface TimelineSegment {
		label: string;
		percent: number;
	}

	interface ProgramStep {
		label: string;
		title: string;
		body: RichTextSegment[];
	}

	interface Props {
		title: string;
		subtitle: string;
		timelineLead: string;
		timelineAlt: string;
		timelineSegments: TimelineSegment[];
		timelineNote: RichTextSegment[];
		steps: ProgramStep[];
		backLabel: string;
		nextLabel: string;
		stepCountLabel: (current: number, total: number) => string;
		theme?: SectionTheme;
		id?: string;
	}

	let {
		title,
		subtitle,
		timelineLead,
		timelineAlt,
		timelineSegments,
		timelineNote,
		steps,
		backLabel,
		nextLabel,
		stepCountLabel,
		theme = "light",
		id
	}: Props = $props();

	const segmentWidths: Record<number, string> = {
		45: "basis-[45%]",
		55: "basis-[55%]"
	};

	let value = $state("step-0");
	const activeIndex = $derived(Number(value.slice("step-".length)));
</script>

<Section {theme} {id} data-testid="associate-program">
	<SectionHeader {title} {subtitle} align="left" />

	<div class="max-w-3xl">
		<p class="text-muted-foreground text-lg">{timelineLead}</p>
		<div
			class="border-border mt-6 flex h-3 overflow-hidden rounded border"
			role="img"
			aria-label={timelineAlt}
		>
			{#each timelineSegments as segment, index (segment.label)}
				<div
					class={cn(
						"h-full",
						segmentWidths[segment.percent] ?? "flex-1",
						index === timelineSegments.length - 1
							? "bg-primary"
							: "bg-muted-foreground/40"
					)}
				></div>
			{/each}
		</div>
		<ul class="mt-4 flex flex-wrap gap-x-6 gap-y-2">
			{#each timelineSegments as segment, index (segment.label)}
				<li class="text-muted-foreground flex items-center gap-2 text-sm">
					<span
						class={cn(
							"size-3 rounded-full",
							index === timelineSegments.length - 1
								? "bg-primary"
								: "bg-muted-foreground/40"
						)}
						aria-hidden="true"
					></span>
					{segment.label}
				</li>
			{/each}
		</ul>
		<p class="text-muted-foreground mt-4 text-sm">
			<RichText segments={timelineNote} />
		</p>
	</div>

	<div class="mt-12">
		<Tabs bind:value>
			<TabsList
				class="border-border h-auto w-full flex-wrap justify-start gap-2 rounded-none border-b bg-transparent p-0"
			>
				{#each steps as step, index (step.label)}
					<TabsTrigger
						value="step-{index}"
						class="text-muted-foreground data-[state=active]:text-foreground data-[state=active]:border-primary group flex-none gap-2 rounded-none border-x-0 border-t-0 border-b-2 border-transparent px-2 py-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
					>
						<span
							class="bg-muted text-muted-foreground group-data-[state=active]:bg-primary group-data-[state=active]:text-primary-foreground flex size-6 items-center justify-center rounded-full text-xs font-semibold"
						>
							{index + 1}
						</span>
						{step.label}
					</TabsTrigger>
				{/each}
			</TabsList>
			{#each steps as step, index (step.label)}
				<TabsContent value="step-{index}" class="max-w-3xl pt-8">
					<h3 class="text-foreground text-xl font-semibold">{step.title}</h3>
					<p class="text-muted-foreground mt-3">
						<RichText segments={step.body} />
					</p>
				</TabsContent>
			{/each}
		</Tabs>

		<div class="border-border mt-8 flex items-center justify-between gap-4 border-t pt-6">
			<Button
				variant="outline"
				class="text-foreground"
				aria-disabled={activeIndex === 0}
				onclick={() => {
					if (activeIndex > 0) value = `step-${activeIndex - 1}`;
				}}
			>
				{backLabel}
			</Button>
			<p class="text-muted-foreground text-sm" data-testid="associate-step-count">
				{stepCountLabel(activeIndex + 1, steps.length)}
			</p>
			<Button
				aria-disabled={activeIndex === steps.length - 1}
				onclick={() => {
					if (activeIndex < steps.length - 1) value = `step-${activeIndex + 1}`;
				}}
			>
				{nextLabel}
			</Button>
		</div>
	</div>
</Section>
